<script lang="ts" module>
  const PAGE_SIZE = 20;

  const SEARCH_TYPE_OPTIONS: Array<{
    value: SearchUngroupedBody['type'];
    label: string;
  }> = [
    { value: 'fuzzy', label: 'Liknende' },
    { value: 'startsWith', label: 'Begynner med' },
    { value: 'includes', label: 'Inneholder' },
  ] as const;
</script>

<script lang="ts">
  import { arrowsCircleCounterclockwiseIcon } from '@nrk/core-icons';
  import type {
    DrawEntry,
    Group,
    Participant,
    SearchUngroupedBody,
    Variation,
  } from '@pin/schemas/admin';
  import { untrack } from 'svelte';
  import { SvelteSet } from 'svelte/reactivity';
  import { getExistingDraw } from '../api/draw';
  import { gameInfo$ } from '../api/gameInfo';
  import {
    allGroups$,
    allVariations$,
    createGroup,
    fetchGroupCounts,
    fetchUngrouped,
    searchUngrouped,
  } from '../api/group';
  import { participants$, participantsStats$ } from '../api/participant';
  import {
    clearCorrectGuesses,
    getCorrectGuesses,
    setCorrectGuesses,
  } from '../api/results';
  import GroupDetailDialog from '../components/groups/GroupDetailDialog.svelte';
  import GuessRankingTable, {
    type RankedRow,
  } from '../components/participants/GuessRankingTable.svelte';
  import MaskInfo from '../components/participants/MaskInfo.svelte';
  import UnmaskedInfo from '../components/participants/UnmaskedInfo.svelte';
  import UnmaskWizard from '../components/participants/UnmaskWizard.svelte';
  import BackButton from '../lib/BackButton.svelte';
  import Breadcrumb from '../lib/Breadcrumb.svelte';
  import Icon from '../lib/Icon.svelte';
  import { addToast } from '../stores/toastStore';
  import { p, route } from '../utils/router';
  import { useObservable } from '../utils/rxjs.svelte';
  import { useClock } from '../utils/useClock.svelte';

  const initParams = new URLSearchParams(location.search);

  const updateUrlParam = (key: string, value: string | null) => {
    const url = new URL(location.href);
    if (value === null) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
    history.replaceState({}, '', url);
  };

  const participants = useObservable(participants$, null);
  const groups = useObservable(allGroups$, null);
  const variations = useObservable(allVariations$, null);
  const stats = useObservable(participantsStats$, {
    data: null,
    loading: true,
    failed: false,
    freshUntil: 0,
  });
  const gameInfo = useObservable(gameInfo$, null);
  const clock = useClock();

  const isLocked = $derived(
    gameInfo.current?.state.lockedAt != null &&
      gameInfo.current.state.lockedAt <= clock.now
  );

  const id = $derived((route.params as { id: string }).id);
  const groupId = $derived(
    (route.params as { id: string; groupId?: string }).groupId
  );
  const showGroupDialog = $derived(groupId != null);
  let dialogSearchQuery = $state<string | undefined>(undefined);
  let justCreatedGroupId = $state<string | undefined>(undefined);

  const handleGroupDialogClose = () => {
    dialogSearchQuery = undefined;
    justCreatedGroupId = undefined;
    location.hash = `#/participants/${id}`;
  };

  const participant = $derived(
    participants.current?.find((it: Participant) => it.id === id)
  );

  const isLoading = $derived(participants.current == null);
  const isMissing = $derived(
    participants.current != null && participant == null
  );

  const summary = $derived(stats.current.data?.get(id));
  const guessTotal = $derived(summary?.guessCount);

  const isUnmasked = $derived(participant?.unmaskedName != null);
  let winner = $state<DrawEntry | null>(null);
  let winnerCheckedForId = $state<string | null>(null);

  $effect(() => {
    const currentId = id;
    const p = participant;

    if (winnerCheckedForId === currentId) return;
    if (!p) return;

    winnerCheckedForId = currentId;

    if (p.unmaskedName != null) {
      getExistingDraw(p.id)
        .then((result) => {
          winner = result?.entries.find((e) => e.status === 'winner') ?? null;
        })
        .catch((e) => {
          console.error('Failed to fetch winner:', e);
          winner = null;
        });
    }
  });

  let unmaskMode = $state(false);
  const selectedGroupIds = new SvelteSet<string>();
  let wizardOpen = $state(false);

  const enterUnmaskMode = () => {
    selectedGroupIds.clear();
    for (const r of groupRows.filter((r) => r.isCorrect)) {
      selectedGroupIds.add(r.key.slice('group:'.length));
    }
    unmaskMode = true;
  };

  let groupRows = $state<RankedRow[]>([]);
  let ungroupedRows = $state<RankedRow[]>([]);

  let query = $state(String(initParams.get('q') ?? ''));
  let page = $state(Math.max(1, Number(initParams.get('side') ?? 1) || 1));
  const isSearching = $derived(query.trim().length > 0);

  let queryDraft = $state(String(initParams.get('q') ?? ''));
  let searchType = $state<SearchUngroupedBody['type']>('fuzzy');

  const isDirtyQuery = $derived(queryDraft.trim() !== query.trim());

  let ungroupedExhausted = $state(false);

  let isLoadingRanking = $state(false);
  let rankingFailed = $state(false);

  const rankedRows = $derived.by(() => {
    const sortByCorrectThenCount = (a: RankedRow, b: RankedRow) =>
      Number(b.isCorrect) - Number(a.isCorrect) ||
      b.count - a.count ||
      a.label.localeCompare(b.label, 'nb');

    if (!isSearching) {
      return [...groupRows, ...ungroupedRows].sort(sortByCorrectThenCount);
    }
    return [...[...groupRows].sort(sortByCorrectThenCount), ...ungroupedRows];
  });

  const visibleRows = $derived(
    isSearching
      ? rankedRows
      : rankedRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  );
  const hasMore = $derived(
    isSearching
      ? !ungroupedExhausted
      : rankedRows.length > page * PAGE_SIZE || !ungroupedExhausted
  );
  const groupCount = $derived(groupRows.length);

  const buildGroupRows = (
    allGroups: Group[],
    allVariations: Variation[],
    groupTotals: Map<string, number>,
    correct: ReadonlySet<string>,
    search: string
  ): RankedRow[] => {
    /* A group counts as correct when any one of its spellings does. */
    const correctGroupIds = new Set(
      allVariations
        .filter(({ value }) => correct.has(value))
        .map(({ groupId }) => groupId)
    );
    const needle = search.trim().toLowerCase();
    const matchingGroupIds =
      needle.length === 0
        ? null
        : new Set(
            allVariations
              .filter(({ value }) => value.toLowerCase().includes(needle))
              .map(({ groupId }) => groupId)
          );

    return allGroups
      .map((group) => ({
        key: `group:${group.id}`,
        label: group.name,
        count: groupTotals.get(group.id) ?? 0,
        isCorrect: correctGroupIds.has(group.id),
        isGroup: true,
      }))
      .filter((row) => row.count > 0)
      .filter(
        (row) =>
          matchingGroupIds == null ||
          matchingGroupIds.has(row.key.slice('group:'.length)) ||
          row.label.toLowerCase().includes(needle)
      );
  };

  const loadRanking = async (
    allGroups: Group[],
    allVariations: Variation[],
    participantId: string,
    targetPage: number,
    search: string
  ) => {
    isLoadingRanking = true;
    const trimmed = search.trim();
    const needed = trimmed.length > 0 ? PAGE_SIZE : targetPage * PAGE_SIZE;
    try {
      const [counts, ungrouped, correct] = await Promise.all([
        fetchGroupCounts(participantId),
        trimmed.length > 0
          ? searchUngrouped({
              type: searchType,
              q: trimmed,
              participantId,
              pageNum: targetPage,
              pageSize: needed,
              threshold: 0.9,
            })
          : fetchUngrouped({
              participantId,
              pageNum: 1,
              pageSize: needed,
            }),
        getCorrectGuesses(participantId),
      ]);

      const correctSet = new Set(correct);
      groupRows = buildGroupRows(
        allGroups,
        allVariations,
        new Map(counts.map(({ id, count }) => [id, count])),
        correctSet,
        trimmed
      );
      ungroupedRows = ungrouped.map(
        ({ normalizedName, nameSample, count }) => ({
          key: `name:${normalizedName}`,
          label: nameSample,
          count,
          isCorrect: correctSet.has(normalizedName),
          isGroup: false,
        })
      );
      ungroupedExhausted = ungrouped.length < needed;
      rankingFailed = false;
    } catch (e) {
      console.error(e);
      rankingFailed = true;
    }
    isLoadingRanking = false;
  };

  $effect(() => {
    const g = groups.current;
    const v = variations.current;
    const q = query;
    const p = page;
    const i = id;
    if (g == null || v == null) return;
    void loadRanking(g, v, i, p, q);
  });

  const submitSearch = (event: SubmitEvent) => {
    event.preventDefault();
    const next = queryDraft.trim();
    if (next === query.trim()) return;
    page = 1;
    query = next;
    updateUrlParam('side', null);
    updateUrlParam('q', next.length === 0 ? null : next);
  };

  $effect(() => {
    const fromUrl = query;
    untrack(() => {
      if (queryDraft.trim() !== fromUrl.trim()) queryDraft = fromUrl;
    });
  });

  const refreshRanking = (q: string = query, p: number = page) => {
    if (groups.current == null || variations.current == null) return;
    void loadRanking(groups.current, variations.current, id, p, q);
  };

  const goToPage = (next: number) => {
    const target = Math.max(1, next);
    page = target;
    updateUrlParam('side', target === 1 ? null : String(target));
  };

  const clearSearch = () => {
    queryDraft = '';
    query = '';
    page = 1;
    updateUrlParam('side', null);
    updateUrlParam('q', null);
  };

  const handleCreateGroup = async (name: string, normalizedName: string) => {
    let newGroup: Group;
    try {
      newGroup = await createGroup({
        name,
        variations: [{ value: normalizedName, displayValue: name }],
      });
    } catch (e) {
      console.error(e);
      addToast({ type: 'error', message: `Kunne ikke opprette gruppe` });
      return;
    }
    dialogSearchQuery = normalizedName;
    justCreatedGroupId = newGroup.id;
    location.hash = `#/participants/${id}/group/${newGroup.id}`;
  };

  const handleToggleGroup = (groupId: string) => {
    if (selectedGroupIds.has(groupId)) {
      selectedGroupIds.delete(groupId);
    } else {
      selectedGroupIds.add(groupId);
    }
  };

  const getVariationsForSelectedGroups = (): string[] => {
    if (variations.current == null) return [];
    return variations.current
      .filter((v) => selectedGroupIds.has(v.groupId))
      .map((v) => v.value);
  };

  const allSelectedAlreadyCorrect = $derived.by(() => {
    if (selectedGroupIds.size === 0) return false;
    const selectedRows = groupRows.filter((r) =>
      selectedGroupIds.has(r.key.slice('group:'.length))
    );
    return selectedRows.length > 0 && selectedRows.every((r) => r.isCorrect);
  });

  let isConfirmingCorrect = $state(false);
  let confirmCorrectError = $state<string | null>(null);

  const handleConfirmCorrectGroups = async () => {
    if (selectedGroupIds.size === 0) return;

    if (allSelectedAlreadyCorrect) {
      wizardOpen = true;
      return;
    }

    const names = getVariationsForSelectedGroups();
    if (names.length === 0) return;
    isConfirmingCorrect = true;
    confirmCorrectError = null;
    try {
      await setCorrectGuesses(id, names);
      await refreshRanking();
      wizardOpen = true;
    } catch (e) {
      console.error(e);
      confirmCorrectError = 'Kunne ikke lagre riktige svar.';
    }
    isConfirmingCorrect = false;
  };

  const handleNoCorrectGuesses = async () => {
    isConfirmingCorrect = true;
    confirmCorrectError = null;
    try {
      await clearCorrectGuesses(id);
      await refreshRanking();
      selectedGroupIds.clear();
      wizardOpen = true;
    } catch (e) {
      console.error(e);
      confirmCorrectError = 'Kunne ikke lagre riktige svar.';
    }
    isConfirmingCorrect = false;
  };

  const handleSaveCorrectGroups = async () => {
    const names = getVariationsForSelectedGroups();
    isConfirmingCorrect = true;
    confirmCorrectError = null;
    try {
      if (names.length === 0) {
        await clearCorrectGuesses(id);
      } else {
        await setCorrectGuesses(id, names);
      }
      await refreshRanking();
      unmaskMode = false;
      selectedGroupIds.clear();
    } catch (e) {
      console.error(e);
      confirmCorrectError = 'Kunne ikke lagre riktige svar.';
    }
    isConfirmingCorrect = false;
  };

  const handleCancelUnmaskMode = () => {
    unmaskMode = false;
    selectedGroupIds.clear();
    confirmCorrectError = null;
  };

  interface CorrectGroupInfo {
    name: string;
    count: number;
    percentage: number;
    variantCount: number;
  }

  const getSelectedGroupInfo = (): CorrectGroupInfo[] => {
    if (groups.current == null || variations.current == null) return [];
    return Array.from(selectedGroupIds).map((groupId) => {
      const group = groups.current!.find((g) => g.id === groupId);
      const row = groupRows.find((r) => r.key === `group:${groupId}`);
      const variants = variations.current!.filter((v) => v.groupId === groupId);
      return {
        name: group?.name ?? '',
        count: row?.count ?? 0,
        percentage: guessTotal ? ((row?.count ?? 0) / guessTotal) * 100 : 0,
        variantCount: variants.length,
      };
    });
  };

  const correctGroupsInfo = $derived.by((): CorrectGroupInfo[] => {
    if (groups.current == null || variations.current == null) return [];
    return groupRows
      .filter((r) => r.isCorrect && r.key.startsWith('group:'))
      .map((row) => {
        const groupId = row.key.slice('group:'.length);
        const group = groups.current!.find((g) => g.id === groupId);
        const variants = variations.current!.filter(
          (v) => v.groupId === groupId
        );
        return {
          name: group?.name ?? row.label,
          count: row.count,
          percentage: guessTotal ? (row.count / guessTotal) * 100 : 0,
          variantCount: variants.length,
        };
      });
  });

  const handleWizardCancel = () => {
    // The pipeline may have unmasked the participant before the wizard closed;
    // never leave the page in "edit correct answers" mode in that case.
    if (isUnmasked) {
      void handleWizardComplete();
      return;
    }
    wizardOpen = false;
  };

  const handleWizardComplete = async () => {
    wizardOpen = false;
    unmaskMode = false;
    selectedGroupIds.clear();
    refreshRanking();

    if (participant) {
      try {
        const result = await getExistingDraw(participant.id);
        winner = result?.entries.find((e) => e.status === 'winner') ?? null;
      } catch (e) {
        console.error('Failed to fetch winner after unmask:', e);
      }
    }
  };
</script>

<section class="stack" aria-labelledby="stats-heading">
  <Breadcrumb currentLabel={participant?.maskName} />
  <div class="heading">
    <h2 id="stats-heading">{participant?.maskName ?? 'Deltaker'}</h2>
    <div class="tools">
      <button
        class="btn tertiary refresh"
        class:busy={isLoadingRanking}
        onclick={() => refreshRanking()}
        disabled={isLoadingRanking}
        aria-label="Hent gjett på nytt"
      >
        <Icon svg={arrowsCircleCounterclockwiseIcon} />
      </button>
      <BackButton fallbackHref={p('/')} />
    </div>
  </div>

  <p class="visually-hidden" role="status" aria-live="polite">
    {isLoadingRanking ? 'Henter gjett …' : 'Gjettene er oppdatert.'}
  </p>

  {#if isLoading}
    <p class="message" aria-busy="true">Laster deltaker …</p>
  {:else if isMissing || participant == null}
    <p class="message">Fant ingen deltaker med id {id}.</p>
  {:else}
    <div class="columns">
      <aside class="sidebar">
        <MaskInfo
          {participant}
          guessCount={summary?.guessCount}
          favoriteCount={summary?.favoriteCount}
        />

        {#if isUnmasked}
          <UnmaskedInfo
            {participant}
            correctGroups={correctGroupsInfo}
            {guessTotal}
            {winner}
          />
        {/if}

        {#if unmaskMode}
          <div class="actions unmask-actions">
            <button
              class="btn primary"
              onclick={isUnmasked
                ? handleSaveCorrectGroups
                : handleConfirmCorrectGroups}
              disabled={selectedGroupIds.size === 0 || isConfirmingCorrect}
            >
              {#if isConfirmingCorrect}
                Lagrer …
              {:else if isUnmasked}
                Lagre riktige svar
              {:else if allSelectedAlreadyCorrect}
                Trekk vinner
              {:else}
                Bekreft riktige svar
              {/if}
            </button>

            {#if !isUnmasked}
              {#if selectedGroupIds.size === 0}
                <div class="status-box warning">
                  <p class="status-text">
                    Velg minst én gruppe i listen som riktig svar.
                  </p>
                </div>
              {:else}
                <div class="status-box success">
                  <ul class="selected-groups">
                    {#each getSelectedGroupInfo() as group (group.name)}
                      <li>{group.name} markert som riktig</li>
                    {/each}
                  </ul>
                </div>
              {/if}
            {/if}
            {#if !isUnmasked}
              <button
                class="btn secondary"
                onclick={handleNoCorrectGuesses}
                disabled={isConfirmingCorrect}
              >
                Ingen riktige svar
              </button>
            {/if}

            {#if confirmCorrectError}
              <p class="error-text">{confirmCorrectError}</p>
            {/if}

            <button class="btn tertiary" onclick={handleCancelUnmaskMode}>
              Avbryt
            </button>
          </div>
        {:else if isUnmasked}
          <div class="actions">
            <button class="btn secondary" onclick={enterUnmaskMode}>
              Rediger riktige svar
            </button>
          </div>
        {:else}
          <div class="actions">
            <button
              class="btn primary"
              onclick={enterUnmaskMode}
              disabled={!isLocked}
            >
              Avmasker
            </button>
            {#if !isLocked}
              <p class="lock-hint">
                For å kunne avmaskere må runden være låst.
              </p>
            {/if}
          </div>
        {/if}
      </aside>

      <div class="main">
        {#if rankingFailed}
          <p class="message" role="alert">
            Kunne ikke hente gjettene.
            <button
              class="btn secondary"
              onclick={() => refreshRanking()}
              disabled={isLoadingRanking}
            >
              Prøv igjen
            </button>
          </p>
        {/if}

        <section class="stack" aria-labelledby="ranking-heading">
          <div class="heading">
            <h3 id="ranking-heading">Gjett</h3>
            {#if groupCount > 0}
              <span class="count">{groupCount} grupper</span>
            {/if}
          </div>

          <form class="search" onsubmit={submitSearch}>
            <input
              type="search"
              bind:value={queryDraft}
              placeholder="Søk i gjett og grupper …"
              aria-label="Søk i gjett og grupper"
            />
            <select
              class="search-type-select"
              bind:value={searchType}
              aria-label="Søketype"
            >
              {#each SEARCH_TYPE_OPTIONS as option (option.value)}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
            <button
              class="btn secondary"
              type="submit"
              disabled={!isDirtyQuery}
            >
              Søk
            </button>
            {#if isSearching}
              <button class="btn clear" type="button" onclick={clearSearch}>
                Nullstill
              </button>
            {/if}
          </form>

          {#if isSearching}
            <p class="count">Sortert etter treff, ikke antall.</p>
          {/if}

          {#if rankedRows.length === 0 && rankingFailed}
            <!-- Nothing loaded yet; the retry above is the only useful action. -->
          {:else if rankedRows.length === 0}
            <p class="message">
              {isSearching
                ? `Ingen treff på «${query.trim()}».`
                : 'Ingen gjett på denne masken ennå.'}
            </p>
          {:else}
            <GuessRankingTable
              rows={visibleRows}
              {page}
              pageSize={PAGE_SIZE}
              {guessTotal}
              participantId={id}
              onCreateGroup={handleCreateGroup}
              {unmaskMode}
              selectedGroups={selectedGroupIds}
              onToggleGroup={handleToggleGroup}
            />
          {/if}

          <div class="paging">
            <button
              class="btn secondary"
              onclick={() => goToPage(page - 1)}
              disabled={page === 1 || isLoadingRanking}
            >
              Forrige
            </button>
            <span class="page-label">Side {page}</span>
            <button
              class="btn secondary"
              onclick={() => goToPage(page + 1)}
              disabled={!hasMore || isLoadingRanking}
            >
              Neste
            </button>
          </div>
        </section>
      </div>
    </div>
  {/if}
</section>

{#if showGroupDialog && groupId}
  <GroupDetailDialog
    open={true}
    {groupId}
    initialSearchQuery={dialogSearchQuery}
    justCreated={justCreatedGroupId === groupId}
    onClose={handleGroupDialogClose}
  />
{/if}

{#if wizardOpen && participant}
  <UnmaskWizard
    {participant}
    correctGroups={getSelectedGroupInfo()}
    onComplete={handleWizardComplete}
    onCancel={handleWizardCancel}
  />
{/if}

<style>
  .heading {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--space-3);
  }

  .tools {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-inline-start: auto;
  }

  .refresh {
    padding-inline: var(--space-2);
  }

  .refresh.busy :global(svg) {
    animation: spin 1s linear infinite;
  }

  .search-type-select {
    width: max-content;
  }

  @keyframes spin {
    to {
      rotate: -1turn;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .refresh.busy :global(svg) {
      animation: none;
      opacity: 0.5;
    }
  }

  .count {
    color: var(--text-muted);
    font-size: var(--text-sm);
  }

  .search {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    & input {
      flex: 1 1 auto;
      min-inline-size: 0;
    }
  }

  .message {
    color: var(--text-muted);
  }
  .columns {
    display: grid;
    grid-template-columns: 20rem minmax(0, 1fr);
    gap: var(--space-6);
    align-items: start;
  }

  .sidebar,
  .main {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    min-inline-size: 0;
  }

  .actions {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    & :global(.btn) {
      justify-content: center;
    }
  }

  .unmask-actions {
    padding: var(--space-3);
    background: var(--surface-raised);
    border-radius: var(--radius-md);
  }

  .status-box {
    padding: var(--space-3);
    border-radius: var(--radius-md);
    border-inline-start: 3px solid;

    &.warning {
      background: light-dark(
        oklch(from var(--warning) 95% calc(c * 0.3) h),
        oklch(from var(--warning) 25% calc(c * 0.4) h)
      );
      border-color: var(--warning);
    }

    &.success {
      background: light-dark(
        oklch(from var(--success) 95% calc(c * 0.3) h),
        oklch(from var(--success) 25% calc(c * 0.4) h)
      );
      border-color: var(--success);
    }
  }

  .status-text {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text);
  }

  .selected-groups {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--text-sm);
    color: var(--text);
  }

  .error-text {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--danger);
  }

  .lock-hint {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text-muted);
    text-align: left;
    font-style: italic;
  }

  .paging {
    display: flex;
    align-items: center;
    gap: var(--space-3);
  }

  .page-label {
    color: var(--text-muted);
    font-size: var(--text-sm);
  }
  @media (width < 50rem) {
    .columns {
      grid-template-columns: minmax(0, 1fr);
    }

    .actions {
      flex-direction: row;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
    }

    .actions > :global(.btn) {
      flex: 0 0 auto;
    }

    .lock-hint {
      order: 3;
      width: 100%;
      margin-block-start: var(--space-2);
    }
  }
</style>
